<template>
  <div class="lesson-preview" v-loading="loading">
    <div v-if="lesson" class="preview-container">
      <!-- Left Panel - Resources -->
      <div class="left-panel">
        <div v-if="mediaResources.length > 0" class="resources-section">
          <el-tabs v-model="activeResourceTab" type="card">
            <el-tab-pane
              v-for="(resource, index) in mediaResources"
              :key="index"
              :name="`resource-${index}`"
            >
              <span slot="label">
                <i
                  :class="getResourceIcon(resource.type)"
                  style="margin-right: 5px"
                ></i>
                {{ getResourceLabel(resource.type, index) }}
              </span>
              <ResourcePreview :resource="resource" />
            </el-tab-pane>
          </el-tabs>
        </div>
        <div v-else class="empty-resources">
          <el-empty :description="$t('NO_RESOURCES')" />
        </div>
      </div>

      <!-- Right Panel - Title and Content -->
      <div class="right-panel">
        <div class="lesson-header">
          <h2 class="lesson-title">{{ lesson.name }}</h2>
        </div>
        <div class="lesson-content-section">
          <div
            v-if="lesson.content"
            class="lesson-content"
            v-html="lesson.content"
          ></div>
          <div v-else class="empty-content">
            <el-empty :description="$t('NO_CONTENT')" />
          </div>
        </div>

        <!-- Document Resources (if any) -->
        <div v-if="documentResources.length > 0" class="documents-section">
          <h3 class="section-title">{{ $t('DOCUMENTS') }}</h3>
          <div class="documents-list">
            <div
              v-for="(doc, index) in documentResources"
              :key="index"
              class="document-item"
            >
              <ResourcePreview :resource="doc" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!loading" class="empty-lesson">
      <el-empty :description="$t('NO_LESSON_DATA')" />
    </div>
  </div>
</template>

<script>
import examVaultService from '@/api/exam-vault'
import ResourcePreview from './ResourcePreview.vue'

export default {
  name: 'LessonPreview',
  components: {
    ResourcePreview
  },
  props: {
    examVaultId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      lesson: null,
      activeResourceTab: 'resource-0'
    }
  },

  computed: {
    mediaResources() {
      if (!this.lesson || !this.lesson.resources) return []
      return this.lesson.resources.filter(
        (r) => r.type === 'video' || r.type === 'audio' || r.type === 'image'
      )
    },

    documentResources() {
      if (!this.lesson || !this.lesson.resources) return []
      return this.lesson.resources.filter((r) => r.type === 'document')
    }
  },

  created() {
    this.fetchLesson()
  },

  watch: {
    examVaultId: {
      handler() {
        this.fetchLesson()
      },
      immediate: true
    }
  },

  methods: {
    async fetchLesson() {
      // Clear previous lesson data to ensure fresh render
      this.lesson = null
      this.loading = true
      try {
        const response = await examVaultService.getLessonByExamVaultId(
          this.examVaultId
        )
        if (response.data && response.data.lesson) {
          this.lesson = response.data.lesson
          // Set first resource as active by default
          if (this.mediaResources.length > 0) {
            this.activeResourceTab = 'resource-0'
          }
        } else {
          this.lesson = null
        }
      } catch (error) {
        console.error('Failed to fetch lesson:', error)
        this.lesson = null
      } finally {
        this.loading = false
      }
    },

    getResourceIcon(type) {
      const icons = {
        video: 'el-icon-video-camera',
        audio: 'el-icon-headset',
        image: 'el-icon-picture',
        document: 'el-icon-document'
      }
      return icons[type] || 'el-icon-file'
    },

    getResourceLabel(type, index) {
      const labels = {
        video: this.$t('VIDEO'),
        audio: this.$t('AUDIO'),
        image: this.$t('IMAGE'),
        document: this.$t('DOCUMENT')
      }
      return `${labels[type] || type} ${index + 1}`
    }
  }
}
</script>

<style scoped lang="scss">
.lesson-preview {
  width: 100%;
  min-height: 400px;

  .preview-container {
    display: flex;
    gap: 24px;
    width: 100%;

    .left-panel {
      flex: 0 0 50%;
      max-width: 50%;

      .resources-section {
        width: 100%;

        :deep(.el-tabs__header) {
          margin-bottom: 15px;
        }

        :deep(.el-tabs__content) {
          padding: 10px 0;
        }
      }

      .empty-resources {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 300px;
        background-color: #f5f7fa;
        border-radius: 8px;
      }
    }

    .right-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .lesson-header {
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 2px solid #e4e7ed;

        .lesson-title {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #303133;
          line-height: 1.4;
        }
      }

      .lesson-content-section {
        flex: 1;
        overflow-y: auto;
        margin-bottom: 20px;
        max-height: 500px;
        padding-right: 10px;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .lesson-content {
          font-size: 15px;
          line-height: 1.8;
          color: #606266;

          :deep(h1),
          :deep(h2),
          :deep(h3),
          :deep(h4),
          :deep(h5),
          :deep(h6) {
            margin-top: 20px;
            margin-bottom: 10px;
            font-weight: 600;
          }

          :deep(p) {
            margin-bottom: 12px;
          }

          :deep(img) {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
          }

          :deep(ul),
          :deep(ol) {
            margin-left: 20px;
            margin-bottom: 12px;
          }
        }

        .empty-content {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 200px;
          background-color: #f5f7fa;
          border-radius: 8px;
        }
      }

      .documents-section {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #e4e7ed;

        .section-title {
          margin: 0 0 15px 0;
          font-size: 18px;
          font-weight: 600;
          color: #303133;
        }

        .documents-list {
          display: flex;
          flex-direction: column;
          gap: 10px;

          .document-item {
            width: 100%;
          }
        }
      }
    }
  }

  .empty-lesson {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }

  @media (max-width: 992px) {
    .preview-container {
      flex-direction: column;

      .left-panel {
        flex: 1;
        max-width: 100%;
      }

      .right-panel {
        flex: 1;
      }
    }
  }
}
</style>
